import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const READER_ID = "arbe-barcode-reader";

export default function BarcodeScanner({ open, onClose, onResult }) {
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setStarting(true);

    // Radix Dialog mounts via portal — the #arbe-barcode-reader div may not be
    // in the DOM yet when this effect runs. Poll for it with rAF (max ~1s).
    const waitForReaderEl = () =>
      new Promise((resolve, reject) => {
        let tries = 0;
        const tick = () => {
          if (cancelled) return reject(new Error("cancelled"));
          if (document.getElementById(READER_ID)) return resolve();
          if (tries++ > 60) return reject(new Error("Scanner viewport did not mount in time"));
          requestAnimationFrame(tick);
        };
        tick();
      });

    const start = async () => {
      try {
        await waitForReaderEl();
        if (cancelled) return;
        if (!scannerRef.current) scannerRef.current = new Html5Qrcode(READER_ID, { verbose: false });

        await scannerRef.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 280, height: 160 }, aspectRatio: 1.5 },
          (decodedText) => {
            if (cancelled) return;
            cancelled = true;
            // stop then return
            scannerRef.current.stop().finally(() => {
              onResult(decodedText);
            });
          },
          () => {}, // per-frame failure callback (ignored)
        );
      } catch (e) {
        if (cancelled) return;
        setError(
          (e && (e.message || e.toString())) ||
            "Cannot access camera. Please allow camera permission in your browser settings.",
        );
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      scannerRef.current = null;
      if (!inst) return;
      // html5-qrcode's stop() throws synchronously if it was never started
      // (e.g. when camera permission was denied). Guard with try/catch around
      // the call itself, not just the returned promise.
      try {
        const p = inst.stop();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch (_) { /* never-started instance */ }
      try { inst.clear(); } catch (_) { /* not mounted */ }
    };
  }, [open, onResult]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="barcode-scanner-dialog">
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="h-5 w-5 text-[#2F5233]" /> Scan barcode
          </DialogTitle>
        </DialogHeader>
        <div className="px-5 text-xs text-[#4A5D5A] leading-snug">
          Point the camera at the barcode on the packaging or the scale-printed sticker.
        </div>

        <div className="relative bg-black mx-5 my-4 rounded-lg overflow-hidden aspect-[3/2]" data-testid="barcode-scanner-viewport">
          <div id={READER_ID} className="w-full h-full" />
          {/* Corner frame overlay */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-[70%] h-[55%] border-2 border-[#F3C97A] rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
          </div>
          {starting && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              Starting camera…
            </div>
          )}
        </div>

        {error && (
          <div className="mx-5 mb-4 p-3 rounded-lg bg-[#FDECEA] text-[#A6231A] text-xs flex items-start gap-2" data-testid="barcode-scanner-error">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 px-5 pb-5">
          <Button variant="outline" onClick={onClose} data-testid="barcode-scanner-cancel">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
